/**
 * Skeleton shapes — the cards, tiles, rows and the page shell that the
 * per-page layouts in pageSkeletons.tsx are assembled from.
 *
 * Every shape is a few grey bars inside a white rounded card, mirroring the
 * real card it stands in for (.cs-reward-card, .cs-kpi, .cs-app-row,
 * .cs-saved-card, .cs-stat-card). `Shell` is CreatorStudioPage's frame: the
 * chrome or back bar, `.cs-page-container` at the route's own max-width, and
 * the title block. Styles: app/styles/creator-skeleton.css. Nothing here reads
 * the viewport — the desktop/mobile fork is CSS only, like the shell's.
 */
import type { CSSProperties, ReactNode } from 'react';
import logo from '~/static/sfn_logo.png';
import { Skeleton, SkeletonText } from '~/components/ui/creator_studio/skeleton';

export const cssVars = (map: Record<string, string>) => map as CSSProperties;

export interface CardProps {
  dark?: boolean;
  className?: string;
  minHeight?: number;
  children: ReactNode;
}

export function Card({ dark, className, minHeight, children }: CardProps) {
  const cls = ['cs-skel-card', dark ? 'cs-skel-card--dark' : '', className ?? ''].filter(Boolean).join(' ');
  return <div className={cls} style={minHeight ? { minHeight } : undefined}>{children}</div>;
}

/** Title bar over a few lines — the default card body. */
export function TextCard({ lines = 2, ...card }: Omit<CardProps, 'children'> & {lines?: number;}) {
  return (
    <Card {...card}>
      <Skeleton width="45%" height={18} radius={6} />
      <SkeletonText lines={lines} />
    </Card>);

}

/** Icon + title, lines, a pill at the foot — the reward / brand card. */
export function IconCard() {
  return (
    <Card>
      <div className="cs-skel-row">
        <Skeleton width={44} height={44} radius={12} />
        <SkeletonText lines={2} widths={['60%', '35%']} />
      </div>
      <SkeletonText lines={2} />
      <Skeleton width={112} height={34} radius={60} />
    </Card>);

}

/** Avatar, two lines, a pill — a table row. */
export function RowCard() {
  return (
    <Card className="cs-skel-card--row">
      <div className="cs-skel-row">
        <Skeleton width={36} height={36} radius="50%" />
        <SkeletonText lines={2} lineHeight={11} widths={['42%', '26%']} />
        <Skeleton width={72} height={26} radius={60} />
      </div>
    </Card>);

}

/** One saved idea: the band and the pill on desktop, a row on the phone. */
export function SavedCardSkeleton() {
  return (
    <Card>
      <Skeleton className="cs-skel-only-desktop cs-hide-mobile" height={96} radius={12} />
      <div className="cs-skel-row">
        <SkeletonText lines={2} widths={['70%', '40%']} />
        <Skeleton width={36} height={36} radius="50%" />
      </div>
    </Card>);

}

/** The stat card the list pages pin to the header's right. */
export function Stat() {
  return (
    <Card className="cs-skel-stat">
      <Skeleton width={72} height={32} radius={6} />
      <Skeleton width={120} height={10} radius={5} />
    </Card>);

}

/** A PillRow: the filter tabs above every list. */
export function Pills({ count = 4 }: {count?: number;}) {
  const widths = [96, 120, 84, 108, 92];
  return (
    <div className="cs-skel-pills">
      {Array.from({ length: count }, (_, i) =>
      <Skeleton key={i} width={widths[i % widths.length]} height={34} radius={60} />
      )}
    </div>);

}

/** KpiGrid: `cols` on desktop, two-up on the phone. */
export function Kpis({ count, cols, className }: {count: number;cols: number;className?: string;}) {
  return (
    <div
      className={`cs-kpi-grid cs-skel-kpis${className ? ` ${className}` : ''}`}
      style={cssVars({ '--cs-kpi-cols': String(cols) })}>

      {Array.from({ length: count }, (_, i) =>
      <div key={i} className="cs-kpi cs-skel-kpi">
          <Skeleton width="40%" height={26} radius={6} />
          <Skeleton width="70%" height={10} radius={5} />
        </div>
      )}
    </div>);

}

export function Rows({ count }: {count: number;}) {
  return (
    <div className="cs-skel-rows">
      {Array.from({ length: count }, (_, i) => <RowCard key={i} />)}
    </div>);

}

/** An auto-fill card grid; `min` is the route grid's column minimum. */
export function Cards({ count, min, className }: {count: number;min: number;className?: string;}) {
  return (
    <div className={`cs-skel-cards${className ? ` ${className}` : ''}`} style={cssVars({ '--cs-skel-min': `${min}px` })}>
      {Array.from({ length: count }, (_, i) => <IconCard key={i} />)}
    </div>);

}

export function Stack({ count, className, minHeight }: {count: number;className?: string;minHeight?: number;}) {
  return (
    <div className={`cs-skel-stack${className ? ` ${className}` : ''}`}>
      {Array.from({ length: count }, (_, i) => <TextCard key={i} minHeight={minHeight} />)}
    </div>);

}

/** Two desktop columns; `cols` and `gap` are the route's own grid values. */
export function Cols({ cols, gap, className, children }: {cols: string;gap: string;className?: string;children: ReactNode;}) {
  return (
    <div
      className={`cs-skel-cols${className ? ` ${className}` : ''}`}
      style={cssVars({ '--cs-skel-cols': cols, '--cs-skel-gap': gap })}>

      {children}
    </div>);

}

/** The 64px bar at >= 1024 and the back bar below it, both as placeholders. */
export function SkeletonChrome() {
  return (
    <>
      <div className="cs-skel-chrome cs-hide-mobile" data-testid="skeleton-chrome">
        <div className="cs-skel-chrome-logo">
          <img src={logo} alt="" />
          <span className="cs-skel-chrome-logo-text">
            <span className="cs-skel-chrome-wordmark">SFN Ai</span>
            <span className="cs-skel-chrome-kicker">Creator Studios</span>
          </span>
        </div>
        <div className="cs-skel-chrome-spacer" />
        <div className="cs-skel-chrome-actions">
          <Skeleton width={128} height={36} radius={60} />
          <Skeleton width={36} height={36} radius="50%" />
          <Skeleton width={168} height={36} radius={60} />
        </div>
      </div>
      <div className="cs-skel-backbar cs-hide-desktop" data-testid="skeleton-backbar">
        <Skeleton width={64} height={14} radius={6} />
        <Skeleton width={28} height={28} radius="50%" />
      </div>
    </>);

}

export interface ShellProps {
  /** Mirrored to `data-skeleton` so tests and styles can tell the pages apart. */
  kind: string;
  /** The route's `maxWidth`, so the container matches the loaded page. */
  maxWidth: number;
  /** `aside` adds the stat card the list pages pin to the header's right. */
  header?: 'none' | 'title' | 'aside';
  children: ReactNode;
}

export function Shell({ kind, maxWidth, header = 'title', children }: ShellProps) {
  return (
    <div className="cs-skel-page" data-skeleton={kind} aria-hidden="true">
      <SkeletonChrome />
      <div className="cs-page-container cs-skel-container" style={cssVars({ '--cs-page-max': `${maxWidth}px` })}>
        {header !== 'none' &&
        <header className="cs-page-header cs-skel-header">
            <div className="cs-page-header-text cs-skel-header-text">
              <Skeleton className="cs-skel-eyebrow" width={72} height={9} radius={4} />
              <Skeleton width="min(320px, 70%)" height={32} radius={8} style={{ marginTop: 8 }} />
              <Skeleton width="min(420px, 90%)" height={12} radius={6} style={{ marginTop: 10 }} />
            </div>
            {header === 'aside' &&
          <div className="cs-page-header-aside cs-skel-header-aside"><Stat /></div>
          }
          </header>
        }
        {children}
      </div>
    </div>);

}